import type { MoneyMinor, NutritionDto, OfferDto } from "@deliveryapp/shared";

export interface OfferPromotion {
  compareAtPrice: MoneyMinor;
  savings: MoneyMinor;
  /** Null pentru reduceri sub 1%; UI-ul arată suma, fără să exagereze procentul. */
  percent: number | null;
}

/** Prețul public autoritar; fallback-ul ține web-ul compatibil cu un core vechi. */
export function offerCustomerUnitPrice(offer: OfferDto): MoneyMinor {
  return Number.isSafeInteger(offer.customerUnitPrice) && (offer.customerUnitPrice as number) >= 0
    ? offer.customerUnitPrice as MoneyMinor
    : offer.priceGross;
}

/**
 * Promoția este derivată exclusiv din cele două prețuri publicate de core.
 * Un `compareAtPrice` lipsă, egal sau mai mic NU produce niciun badge: vitrina
 * nu inventează reduceri și nu transformă o eroare de catalog într-o ofertă.
 */
export function offerPromotion(offer: OfferDto): OfferPromotion | null {
  const customerPrice = offerCustomerUnitPrice(offer);
  const declaredCompareAt = offer.compareAtPrice;
  const compareAt = Number.isSafeInteger(declaredCompareAt) && (declaredCompareAt as number) > customerPrice
    ? declaredCompareAt as MoneyMinor
    : Number.isSafeInteger(offer.platformSubsidy) && (offer.platformSubsidy as number) > 0 && offer.priceGross > customerPrice
      ? offer.priceGross
      : null;
  if (compareAt === null) return null;
  const savings = compareAt - customerPrice;
  return {
    compareAtPrice: compareAt as MoneyMinor,
    savings,
    percent: (() => {
      const wholePercent = Math.min(99, Math.floor((savings / (compareAt as number)) * 100));
      return wholePercent >= 1 ? wholePercent : null;
    })(),
  };
}

export type OfferFilter = "all" | "promotions" | "favorites" | "vegan" | "vegetarian" | "gluten-free";

export interface OfferFilterOption {
  id: OfferFilter;
  label: string;
  icon: string;
}

const DIET_FILTERS: Array<{ id: Exclude<OfferFilter, "all" | "promotions" | "favorites">; label: string; icon: string }> = [
  { id: "vegan", label: "Vegan", icon: "🌱" },
  { id: "vegetarian", label: "Vegetarian", icon: "🥬" },
  { id: "gluten-free", label: "Fără gluten", icon: "◇" },
];

function canonical(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[_–—]+/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

export function normalizedDietTag(value: string): OfferFilter | null {
  const tag = canonical(value);
  if (tag === "vegan" || tag.includes("100% vegetal")) return "vegan";
  if (tag === "vegetarian" || tag === "vegetariană" || tag === "vegetariana") return "vegetarian";
  if (
    tag === "gluten-free" ||
    tag === "gluten free" ||
    tag === "fara gluten" ||
    tag === "fără gluten"
  ) {
    return "gluten-free";
  }
  return null;
}

/** Filtrele apar doar dacă meniul conține date reale pentru ele. */
export function availableOfferFilters(offers: OfferDto[]): OfferFilterOption[] {
  const result: OfferFilterOption[] = [
    { id: "all", label: "Toate", icon: "" },
    { id: "favorites", label: "Favorite", icon: "♡" },
  ];
  if (offers.some((offer) => offerPromotion(offer) !== null)) {
    result.splice(1, 0, { id: "promotions", label: "Oferte", icon: "%" });
  }
  for (const definition of DIET_FILTERS) {
    if (offers.some((offer) => offer.dietTags.some((tag) => normalizedDietTag(tag) === definition.id))) {
      result.push(definition);
    }
  }
  return result;
}

export function offerMatchesFilter(offer: OfferDto, filter: OfferFilter, favorites: ReadonlySet<string>): boolean {
  if (filter === "all") return true;
  if (filter === "promotions") return offerPromotion(offer) !== null;
  if (filter === "favorites") return favorites.has(offer.offerId);
  return offer.dietTags.some((tag) => normalizedDietTag(tag) === filter);
}

export function friendlyDietTag(value: string): string {
  const known = normalizedDietTag(value);
  if (known === "gluten-free") return "Fără gluten";
  if (known === "vegetarian") return "Vegetarian";
  if (known === "vegan") return "Vegan";
  return value.trim();
}

export interface NutritionRow {
  label: string;
  value: string;
}

function amount(value: number | null | undefined, suffix: string): string | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return null;
  return `${new Intl.NumberFormat("ro-RO", { maximumFractionDigits: 2 }).format(value)} ${suffix}`;
}

/** Nu completează valori necunoscute cu zero; returnează doar ce a declarat catalogul. */
export function nutritionRows(nutrition: NutritionDto | null | undefined): NutritionRow[] {
  if (nutrition === null || nutrition === undefined) return [];
  const energy = [amount(nutrition.energyKj, "kJ"), amount(nutrition.energyKcal, "kcal")]
    .filter((entry): entry is string => entry !== null)
    .join(" / ");
  const candidates: Array<[string, string | null]> = [
    ["Energie", energy.length > 0 ? energy : null],
    ["Grăsimi", amount(nutrition.fat, "g")],
    ["din care acizi grași saturați", amount(nutrition.saturatedFat, "g")],
    ["Carbohidrați", amount(nutrition.carbohydrates, "g")],
    ["din care zaharuri", amount(nutrition.sugars, "g")],
    ["Fibre", amount(nutrition.fibre, "g")],
    ["Proteine", amount(nutrition.protein, "g")],
    ["Sare", amount(nutrition.salt, "g")],
  ];
  return candidates.flatMap(([label, value]) => (value === null ? [] : [{ label, value }]));
}

/** Baza declarației se afișează numai dacă a venit explicit și este validă. */
export function nutritionBasisLabel(nutrition: NutritionDto | null | undefined): string {
  const basis = nutrition?.basisGrams;
  return typeof basis === "number" && Number.isFinite(basis) && basis > 0
    ? `per ${new Intl.NumberFormat("ro-RO", { maximumFractionDigits: 2 }).format(basis)} g`
    : "bază nedeclarată";
}

export function formatNetWeight(offer: OfferDto): string | null {
  const weight = offer.netWeight;
  if (weight === null || weight === undefined || !Number.isFinite(weight.amount) || weight.amount <= 0) return null;
  return `${new Intl.NumberFormat("ro-RO", { maximumFractionDigits: 2 }).format(weight.amount)} ${weight.unit}`;
}
